/**
 * Guide anchors — the controls Guide may point at (plan
 * guide-can-walk-a-person-through-a-task-highlight, item guide-anchors;
 * design: examplebrain note 665e8b13, docs/architecture/guide-anchors.md).
 *
 * An anchor is a stable dotted name (`msgraph.email.add-mail-app`) declared
 * next to its feature in shared/features/<feature>.anchors.ts, with a label,
 * the page it lives on and how the product observes that the person did it:
 *
 *   - `click`: the person clicked the marked element;
 *   - `route`: the address reached the given path and query;
 *   - `state`: a named state read — one of the feature's EXISTING reads (a
 *     route contract the page already calls, by the name of its contract and
 *     its client function) and a pure test of its answer. A new-item read
 *     lists the ids of the items the answer holds instead: it is done while
 *     an id is listed that was not listed when the tour started, so a step
 *     that creates something is done only by the new item;
 *   - `visible`: the named anchor's element (or the step's own when omitted)
 *     is on the page and visible — a dialog or section being on screen;
 *   - `filled`: the named anchor's field (or the step's own when omitted) has
 *     a value — an input, textarea or select that is not empty, or a checkbox
 *     or radio that is checked. Only fields can be filled; any other element
 *     is never filled.
 *
 * Pages mark the element with the frontend helper guideAnchor(name, key?)
 * (frontend/src/platform/guide/guideAnchor.js), which renders
 * data-guide-anchor="<name>" only for declared names. Guide never receives
 * or produces selectors: only anchor names, resolved against the registry
 * (shared/guideAnchorRegistry.ts) of the running release.
 *
 * Pure TypeScript with one type import, consumable by the worker, the
 * frontend and the catalogue generator alike.
 */

import type { EndpointContract, FeatureIdentity } from './featureModule'

/**
 * Query parameters a page or a route completion requires: a string must be
 * equal, `true` present with any value, `null` absent.
 */
export type GuideQueryMatch = Readonly<Record<string, string | true | null>>

/** Where an anchor lives: a console route path and the query it needs. */
export interface GuidePage {
  readonly path: string
  readonly params?: GuideQueryMatch
}

export type GuideCompletion<R extends string = string> =
  | { readonly kind: 'click' }
  | { readonly kind: 'route'; readonly path: string; readonly params?: GuideQueryMatch }
  | { readonly kind: 'state'; readonly read: R }
  | { readonly kind: 'visible'; readonly anchor?: string }
  | { readonly kind: 'filled'; readonly anchor?: string }

export interface GuideAnchor<R extends string = string> {
  /** What the control is, in the console's words. */
  readonly label: string
  readonly page: GuidePage
  readonly completion: GuideCompletion<R>
  /**
   * The control's help line: one short plain sentence the page shows next to
   * the control (its hint, note or summary) and Guide reads. Declared once
   * here; the page renders it with guideAnchorHelp(name)
   * (shared/guideAnchorRegistry.ts) instead of its own copy. Declared only
   * where the page shows such a line.
   */
  readonly help?: string
  /**
   * Several elements carry this anchor, one per item (e.g. one Approve per
   * area), told apart by data-guide-anchor-key. A keyed `state` completion
   * reads the same key.
   */
  readonly keyed?: boolean
}

/** A named state read as a feature declares it. */
export interface GuideStateReadDeclaration<C extends string = string> {
  /** What "done" means, in words. */
  readonly label: string
  /** The feature's existing read: its route contract key, which is also its frontend client function's name. */
  readonly contract: C
  /** The read takes the anchor's key (a keyed anchor's item). */
  readonly keyed?: boolean
  /**
   * Done, from the read's answer (the contract's response body). Keyed reads
   * get the item's key; without one they answer for any item. (Method
   * syntax, so a declaration may type the answer it reads.) A read declares
   * either `done` or `items`.
   */
  done?(data: unknown, key?: string): boolean
  /**
   * A new-item read: the ids of the items the answer lists (the person's
   * own, where the list also holds others'). The tour keeps the ids listed
   * when it started; the step is done while another id is listed, and a
   * keyed step without a key of its own follows that item. Never keyed.
   */
  items?(data: unknown): readonly string[]
}

/** A named state read with the existing route it re-reads. */
export interface GuideStateRead extends GuideStateReadDeclaration {
  readonly feature: string
  readonly method: EndpointContract['method']
  readonly path: string
}

/** A help line is one short sentence of plain text. */
export const GUIDE_HELP_MAX = 200

const NAME = /^[a-z][a-z0-9_]*(\.[a-z0-9]+(-[a-z0-9]+)*)+$/

function checkName(feature: FeatureIdentity, kind: string, name: string): void {
  if (!NAME.test(name)) throw new Error(`guide ${kind} "${name}": a dotted lower-case name is required (feature.part.part)`)
  if (!name.startsWith(`${feature.name}.`)) throw new Error(`guide ${kind} "${name}": must start with its feature "${feature.name}."`)
}

function checkHelp(where: string, help: string): void {
  if (typeof help !== 'string' || !help.trim() || help !== help.trim()) throw new Error(`${where}: help must be non-empty text without surrounding spaces`)
  if (help.length > GUIDE_HELP_MAX) throw new Error(`${where}: help is longer than ${GUIDE_HELP_MAX} characters`)
  if (/[\n<>]/.test(help)) throw new Error(`${where}: help is one line of plain text (no line breaks or markup)`)
}

function checkQuery(where: string, params: GuideQueryMatch | undefined): void {
  for (const [key, value] of Object.entries(params ?? {})) {
    if (!key || (typeof value !== 'string' && value !== true && value !== null)) throw new Error(`${where}: query "${key}" must be a string, true or null`)
  }
}

function checkPath(where: string, path: string): void {
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('?') || path.includes('#')) {
    throw new Error(`${where}: "${path}" must be a console path without query or fragment`)
  }
}

function checkObservedAnchor(where: string, anchor: string | undefined): void {
  if (anchor === undefined) return
  if (!NAME.test(anchor)) throw new Error(`${where}: observed anchor "${anchor}" must be a dotted lower-case name`)
}

/**
 * A feature's named state reads. Each names one of the feature's existing
 * route contracts; the method and path are taken from it, so a read that no
 * longer exists fails to type-check and to load.
 */
export function defineGuideStateReads<C extends Readonly<Record<string, EndpointContract>>, N extends string>(
  feature: FeatureIdentity,
  contracts: C,
  reads: { readonly [K in N]: GuideStateReadDeclaration<keyof C & string> },
): { readonly [K in N]: GuideStateRead } {
  const out = {} as Record<N, GuideStateRead>
  for (const name of Object.keys(reads) as N[]) {
    checkName(feature, 'state read', name)
    const read = reads[name]
    const contract = contracts[read.contract]
    if (!contract) throw new Error(`guide state read "${name}": ${feature.name} has no contract "${read.contract}"`)
    if (!read.label) throw new Error(`guide state read "${name}": a label is required`)
    if ((typeof read.done === 'function') === (typeof read.items === 'function')) {
      throw new Error(`guide state read "${name}": declare exactly one of done or items`)
    }
    if (read.items && read.keyed) throw new Error(`guide state read "${name}": a new-item read is never keyed`)
    out[name] = { ...read, feature: feature.name, method: contract.method, path: contract.path }
  }
  return out
}

/**
 * A feature's anchors. `reads` are the state reads a `state` completion may
 * name (the feature's own, or the registry's); a typo in a name fails to
 * type-check.
 */
export function defineGuideAnchors<R extends string, N extends string>(
  feature: FeatureIdentity,
  reads: { readonly [K in R]: GuideStateRead },
  anchors: { readonly [K in N]: GuideAnchor<NoInfer<R>> },
): { readonly [K in N]: GuideAnchor<R> & { readonly feature: string } } {
  const out = {} as Record<N, GuideAnchor<R> & { feature: string }>
  for (const name of Object.keys(anchors) as N[]) {
    checkName(feature, 'anchor', name)
    const anchor = anchors[name]
    const where = `guide anchor "${name}"`
    if (!anchor.label) throw new Error(`${where}: a label is required`)
    if (anchor.help !== undefined) checkHelp(where, anchor.help)
    checkPath(where, anchor.page.path)
    checkQuery(where, anchor.page.params)
    const completion = anchor.completion
    if (completion.kind === 'route') {
      checkPath(where, completion.path)
      checkQuery(where, completion.params)
    } else if (completion.kind === 'state') {
      const read = (reads as Record<string, GuideStateRead>)[completion.read]
      if (!read) throw new Error(`${where}: unknown state read "${completion.read}"`)
      if (Boolean(read.keyed) !== Boolean(anchor.keyed)) throw new Error(`${where}: a keyed anchor needs a keyed state read, and only it`)
    } else if (completion.kind === 'visible' || completion.kind === 'filled') {
      checkObservedAnchor(where, completion.anchor)
    } else if (completion.kind !== 'click') {
      throw new Error(`${where}: unknown completion kind`)
    }
    out[name] = { ...anchor, feature: feature.name }
  }
  return out
}

/** Whether a URL query satisfies a GuideQueryMatch. */
export function guideQueryMatches(match: GuideQueryMatch | undefined, search: URLSearchParams): boolean {
  return Object.entries(match ?? {}).every(([key, value]) => (value === null
    ? !search.has(key)
    : value === true ? search.has(key) : search.get(key) === value))
}
